import { useEffect, useState } from "react";
import { Buton, DenemeNetGrafigi, Iskelet, denemeNoktalari } from "@koclukpanel/ui";
import type { DenemeSonucCiktisi } from "@koclukpanel/contracts";
import { api, denemeTrendi, type DenemeTrendim } from "./api";
import { hataMetni } from "./hata-metni";
import { HedefNetlerim } from "./HedefNetlerim";

type NetTrendi = Omit<DenemeTrendim, "denemeler"> & {
  denemeler: Array<DenemeTrendim["denemeler"][number] & { atamaId?: string }>;
};

/**
 * DENEME NETLERİM — öğrencinin "Denemeler" sekmesinin üstünde (dalga 3, F-EXM-09).
 *
 * react-query KULLANILMIYOR: öğrenci kabuğu sorgu sağlayıcısız çiziliyor
 * (yalnız veli portalı sağlayıcı istiyor — `Uygulama.test.tsx` notu).
 * Sonuçlanmış deneme yoksa HİÇBİR ŞEY çizmiyor: altındaki liste zaten
 * "henüz sonuç yok" diyor; boş bir grafik ikinci kez aynı şeyi söylerdi.
 */
export function DenemeNetlerim({ ogrenciId }: { ogrenciId: string }) {
  const [veri, setVeri] = useState<NetTrendi | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [deneme, setDeneme] = useState(0);

  useEffect(() => {
    let gecerli = true;
    setHata(null);
    setVeri(null);
    void denemeTrendi(ogrenciId).then(
      (v) => {
        if (gecerli) setVeri(v);
      },
      (e: unknown) => {
        if (gecerli) setHata(hataMetni(e, "Net grafiği alınamadı."));
      },
    );
    return () => {
      gecerli = false;
    };
  }, [ogrenciId, deneme]);

  if (hata) {
    return (
      <div
        role="status"
        className="rounded-alan bg-uyari-500/15 text-uyari-600 flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
      >
        <span>{hata}</span>
        <Buton gorunum="ikincil" onClick={() => setDeneme((n) => n + 1)}>
          Tekrar dene
        </Buton>
      </div>
    );
  }
  if (veri === null) return <Iskelet satir={2} />;
  /*
    Hedef kartı deneme sonucundan BAĞIMSIZ: koç ilk denemeden önce de hedef girebilir. Sonuç yokken hedef
    de yoksa (ya da uç hata verdiyse) hiçbir şey çizilmez (`sessiz`) — yukarıdaki "hiçbir şey çizme" kuralı
    korunur.
  */
  if (veri.denemeler.length === 0) return <HedefNetlerim ogrenciId={ogrenciId} sessiz />;
  /*
    F-EXM-09: hedef ve ders farkı sunucudan geliyor (toplam = derslerin toplamı); burada
    hesaplanmıyor. Hedefi koç belirler: bu ekranda düzenleme kontrolü YOK.
  */
  return (
    <div className="flex flex-col gap-2">
      <DenemeNetGrafigi denemeler={denemeNoktalari(veri.denemeler)} baslik="Deneme netlerim" />
      {veri.denemeler.map((satir) => {
        // F-EXM-20 — ayrıntı kimliği sunucudan gelir; başka öğrencinin ataması tahmin edilmez.
        const { atamaId } = satir;
        return atamaId ? <DenemeSonucTablolari key={atamaId} atamaId={atamaId} /> : null;
      })}
      <p className="text-notr-600 text-xs">Hedef netlerini koçun belirler.</p>
      <HedefNetlerim ogrenciId={ogrenciId} />
    </div>
  );
}

interface SonucAyrintisi {
  deneme: { name: string };
  toplam: { net: number };
  duzeltmeBekliyor?: true;
  rank?: number;
  percentile?: number;
  bolumler: Array<{
    sectionId: string;
    ad: string;
    correct: number;
    wrong: number;
    empty: number;
    net: number;
  }>;
  sorular?: DenemeSonucCiktisi["sorular"];
}

function DenemeSonucTablolari({ atamaId }: { atamaId: string }) {
  const [sonuc, setSonuc] = useState<SonucAyrintisi | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yenileme, setYenileme] = useState(0);
  useEffect(() => {
    let gecerli = true;
    setSonuc(null);
    setHata(null);
    void api<SonucAyrintisi>(`/denemeler/sonuclar/${encodeURIComponent(atamaId)}`).then(
      (veri) => {
        if (gecerli) setSonuc(veri);
      },
      (hata: unknown) => {
        if (gecerli) setHata(hataMetni(hata, "Deneme sonucu alınamadı."));
      },
    );
    return () => {
      gecerli = false;
    };
  }, [atamaId, yenileme]);
  if (hata)
    return (
      <div role="status">
        {hata}{" "}
        <Buton gorunum="ikincil" onClick={() => setYenileme((sayi) => sayi + 1)}>
          Tekrar dene
        </Buton>
      </div>
    );
  if (!sonuc) return <Iskelet satir={2} />;
  return (
    <section className="border-notr-200 rounded-alan border p-3">
      <h3 className="font-semibold">{sonuc.deneme.name}</h3>
      {sonuc.duzeltmeBekliyor && <p role="status">Düzeltiliyor</p>}
      <p>Toplam net: {sonuc.toplam.net}</p>
      {/* F-EXM-22 — alanın yokluğu boş bir sıralama kutusuna dönüştürülmez. */}
      {Object.hasOwn(sonuc, "rank") && <p>Kurum içi sıralama: {sonuc.rank}</p>}
      {Object.hasOwn(sonuc, "percentile") && (
        <p>Yüzdelik dilim: %{(sonuc.percentile! * 100).toLocaleString("tr-TR")}</p>
      )}
      <table className="w-full text-left text-sm">
        <caption className="text-left">Ders netleri</caption>
        <thead>
          <tr>
            <th scope="col">Ders</th>
            <th scope="col">Doğru</th>
            <th scope="col">Yanlış</th>
            <th scope="col">Boş</th>
            <th scope="col">Net</th>
          </tr>
        </thead>
        <tbody>
          {sonuc.bolumler.map((bolum) => (
            <tr key={bolum.sectionId}>
              <th scope="row">{bolum.ad}</th>
              <td>{bolum.correct}</td>
              <td>{bolum.wrong}</td>
              <td>{bolum.empty}</td>
              <td>{bolum.net}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* F-EXM-12/20/21 — açılma kararı sunucudadır; tarayıcının saati erişimi değiştirmez. */}
      {sonuc.sorular && (
        <table className="mt-3 w-full text-left text-sm">
          <caption className="text-left">Soru tablosu</caption>
          <thead>
            <tr>
              <th scope="col">Soru</th>
              <th scope="col">Cevabın</th>
              <th scope="col">Doğru cevap</th>
              <th scope="col">Sonuç</th>
            </tr>
          </thead>
          <tbody>
            {sonuc.sorular.map((soru) => (
              <tr key={`${soru.sectionId}-${soru.questionNo}`}>
                <th scope="row">{soru.questionNo}</th>
                <td>{soru.cevap ?? "Boş"}</td>
                <td>{soru.dogruCevap}</td>
                <td>
                  {{ DOGRU: "Doğru", YANLIS: "Yanlış", BOS: "Boş", IPTAL: "İptal" }[soru.sonuc]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
